<template>
  <div class="min-h-screen transition-colors duration-700 pt-20 md:pt-24" :style="{ backgroundColor: pageData.bgColor || pageData.color || '#0A0A0B' }">
    <Navbar :showLangSelector="false" />

    <div :class="{ 'bg-light-theme': !isDarkBg }">
      <!-- Hero/Banner Section -->
      <div v-if="pageData.image && (!isBuilderPage || pageData.type === 'presskit')" 
           class="relative w-full overflow-hidden"
           :class="pageData.type === 'presskit' ? 'h-[30vh] md:h-[40vh]' : 'h-[50vh] md:h-[65vh]'">
        <img :src="pageData.image" class="w-full h-full object-cover scale-105" />
        <div class="absolute inset-0 bg-black/60"></div>
        
        <!-- Overlay Content -->
        <div class="absolute inset-0 flex flex-col items-center text-center px-6"
             :class="pageData.type === 'presskit' ? 'justify-center pb-0' : 'justify-end pb-24'">
          <div class="max-w-4xl animate-fade-in-up">
            <h1 class="tracking-tighter text-white drop-shadow-[0_10px_30px_rgba(0,0,0,0.8)] uppercase"
                :class="pageData.type === 'presskit' ? 'text-3xl md:text-5xl font-medium' : 'text-5xl md:text-8xl font-black'">
              {{ pageData.type === 'presskit' ? 'PRESS KIT' : pageData.title }}
            </h1>
            <div v-if="pageData.type !== 'presskit'" class="flex items-center justify-center gap-4 mt-8 opacity-80">
              <div class="h-[1px] w-12 bg-white/30"></div>
              <span class="text-white text-xs font-black tracking-[0.5em] uppercase">{{ pageData.type }}</span>
              <div class="h-[1px] w-12 bg-white/30"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Body -->
      <div class="w-full relative z-10" :class="isBuilderPage ? 'py-0' : 'py-20'">
        <!-- Layout Especial para Presskit -->
        <div v-if="pageData.type === 'presskit' && parsedBlocks.length > 0" class="builder-page-content max-w-[1200px] mx-auto px-6 space-y-12 !py-12">
          <!-- Grid de Duas Colunas (Ficha Técnica na Esquerda, Outros na Direita) -->
          <div class="presskit-grid">
            <!-- Coluna Esquerda: Ficha Técnica -->
            <div class="presskit-col-left space-y-8">
              <div v-for="fichaBlock in parsedBlocks.filter(b => b.type === 'ficha_tecnica')" :key="fichaBlock.id" class="presskit-factsheet">
                <h2 class="presskit-factsheet-title">{{ fichaBlock.data.title || 'Factsheet' }}</h2>
                <div v-for="(row, idx) in fichaBlock.data.rows" :key="idx" class="presskit-factsheet-item">
                  <span class="presskit-factsheet-key">{{ row.key }}</span>
                  <span class="presskit-factsheet-value" v-html="row.value"></span>
                </div>
              </div>
            </div>

            <!-- Coluna Direita: Bloco Hero, Texto Rico, Galeria, Vídeo, FAQ, CTA -->
            <div class="presskit-col-right space-y-12">
              <!-- Bloco Hero (se houver na página) no topo da coluna direita -->
              <div v-for="heroBlock in parsedBlocks.filter(b => b.type === 'hero')" :key="heroBlock.id" class="space-y-6">
                <!-- Título e Subtítulo do Banner Hero -->
                <div class="space-y-2">
                  <h1 class="text-2xl md:text-4xl font-black text-white tracking-tight">{{ heroBlock.data.title }}</h1>
                  <p v-if="heroBlock.data.subtitle" class="text-base text-slate-400 leading-relaxed">{{ heroBlock.data.subtitle }}</p>
                </div>

                <!-- Imagem de Fundo do Banner Hero (renderizada como tag img proporcional) -->
                <div v-if="heroBlock.data.bgImage" class="w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                  <img :src="heroBlock.data.bgImage" class="w-full h-auto object-contain" />
                </div>

                <!-- Botão de CTA se configurado -->
                <div v-if="heroBlock.data.btnText && heroBlock.data.btnLink" class="pt-2">
                  <a :href="heroBlock.data.btnLink" class="mito-btn-download" target="_blank">{{ heroBlock.data.btnText }}</a>
                </div>
              </div>

              <div v-for="block in parsedBlocks.filter(b => b.type !== 'hero' && b.type !== 'ficha_tecnica')" :key="block.id">
                <!-- Rich Text -->
                <div v-if="block.type === 'rich_text'" class="mito-text-block leading-relaxed text-slate-300 text-base" v-html="block.data.content"></div>

                <!-- Gallery -->
                <div v-else-if="block.type === 'gallery'" class="mito-gallery-block space-y-6">
                  <h2 v-if="block.data.title" class="text-xl font-black text-white uppercase tracking-wider mb-6">{{ block.data.title }}</h2>
                  <div class="mito-gallery-grid-clean">
                    <div v-for="(item, idx) in block.data.items" :key="idx" class="mito-gallery-item-clean">
                      <img :src="item.url" :alt="item.title || 'Screenshot'" class="mito-gallery-img-clean" />
                    </div>
                  </div>
                </div>

                <!-- Video -->
                <div v-else-if="block.type === 'video'" class="mito-video-block space-y-6">
                  <h2 v-if="block.data.title" class="text-xl font-black text-white uppercase tracking-wider mb-6">{{ block.data.title }}</h2>
                  <div class="mito-video-container">
                    <iframe :src="convertToEmbedUrl(block.data.videoUrl)" allowfullscreen></iframe>
                  </div>
                </div>

                <!-- FAQ -->
                <div v-else-if="block.type === 'faq'" class="mito-faq-block space-y-6">
                  <h2 v-if="block.data.title" class="text-xl font-black text-white uppercase tracking-wider mb-6">{{ block.data.title }}</h2>
                  <div class="space-y-4">
                    <div v-for="(item, idx) in block.data.items" :key="idx" class="faq-item p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-purple-500/30 transition-all duration-300">
                      <h4 class="text-sm font-bold text-white uppercase tracking-wider mb-2">{{ item.question }}</h4>
                      <p class="text-xs text-slate-400 leading-relaxed mb-0">{{ item.answer }}</p>
                    </div>
                  </div>
                </div>

                <!-- CTA -->
                <div v-else-if="block.type === 'cta'" class="mito-cta-block text-center p-8 md:p-12 rounded-3xl bg-gradient-to-br from-purple-900/20 to-[#151516] border border-purple-500/10 shadow-xl space-y-4">
                  <h3 class="text-lg md:text-xl font-black text-white uppercase tracking-wider">{{ block.data.title || 'Baixar Conteúdo' }}</h3>
                  <p class="text-xs text-slate-400 max-w-lg mx-auto">{{ block.data.desc }}</p>
                  <div class="pt-4">
                    <a :href="block.data.btnLink || '#'" class="mito-btn-download" target="_blank">{{ block.data.btnText || 'Fazer Download' }}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Layout Padrão -->
        <div 
          v-else-if="pageData.content" 
          :class="isBuilderPage ? 'builder-page-content' : 'custom-page-content'" 
          v-html="pageData.content"
        ></div>

        <!-- Related Content (Sleek Cards) -->
        <div v-if="relatedPages.length > 0 && pageData.type !== 'presskit'" class="max-w-[1100px] mx-auto px-6 mt-40 pt-20 border-t border-white/10">
          <div class="flex items-center justify-between mb-16">
            <h2 class="text-2xl md:text-3xl font-black text-white uppercase tracking-tighter">{{ relatedTitle }}</h2>
            <router-link to="/home" class="text-[10px] font-black text-purple-400 hover:text-white transition-colors tracking-widest">VIEW ALL &rarr;</router-link>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-10">
            <router-link 
              v-for="p in relatedPages" 
              :key="p._id"
              :to="'/pages/' + p.customUrl"
              class="group relative bg-[#151516] rounded-[2.5rem] overflow-hidden border border-white/5 hover:border-purple-500/50 transition-all duration-500 shadow-2xl"
            >
              <div class="aspect-[4/3] relative overflow-hidden">
                <img :src="p.image || '/placeholder.jpg'" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale-[0.5] group-hover:grayscale-0" />
                <div class="absolute inset-0 bg-gradient-to-t from-[#151516] via-[#151516]/20 to-transparent"></div>
              </div>
              <div class="p-8">
                <h3 class="text-xl font-black text-white group-hover:text-purple-400 transition-colors line-clamp-1 uppercase">{{ p.title }}</h3>
                <p class="text-sm text-slate-500 mt-3 line-clamp-2 leading-relaxed">{{ p.description }}</p>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <BottomSectionsNew 
      v-if="page.type === 'presskit'" 
      :language="lang" 
      :showAbout="false"
      :config="homeConfig?.about || {}"
      :socialLinks="homeConfig?.about?.footerSocialLinks || {}"
      :socialIcons="homeConfig?.about?.footerSocialIcons || {}"
      :footerSocialTitle="homeConfig?.about?.footerSocialTitle || ''"
    />
    <Footer v-else />
  </div>
</template>

<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useHead } from '@vueuse/head';
import Navbar from "@/components/sitegame/NavbarNew.vue"; 
import Footer from "@/components/sitegame/Footer.vue";
import BottomSectionsNew from "@/components/sitegame/BottomSectionsNew.vue";
import api from '@/services/api';

const props = defineProps({
  page: { type: Object, required: true },
  lang: { type: String, default: 'pt' }
});

const pageData = computed(() => {
  if (props.lang === 'en' && props.page.en) {
    return { ...props.page, ...props.page.en };
  }
  return props.page;
});

const isBuilderPage = computed(() => {
  return pageData.value.content && pageData.value.content.includes('id="mito-builder-data"');
});

const parsedBlocks = computed(() => {
  if (!pageData.value.content) return [];
  const match = pageData.value.content.match(/<script type="application\/json" id="mito-builder-data">([\s\S]*?)<\/script>/);
  if (match && match[1]) {
    try {
      return JSON.parse(match[1]);
    } catch (e) {
      console.error("Error parsing builder data:", e);
    }
  }
  return [];
});

const convertToEmbedUrl = (url) => {
  if (!url) return '';
  let videoId = '';
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  if (match && match[2].length === 11) {
    videoId = match[2];
  } else {
    return url;
  }
  return `https://www.youtube.com/embed/${videoId}`;
};

const isDarkBg = computed(() => {
  const color = pageData.value.bgColor || pageData.value.color;
  if (!color) return true;
  const hex = color.replace('#', '');
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return ((r * 299) + (g * 587) + (b * 114)) / 1000 < 128;
  } else if (hex.length === 6) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return ((r * 299) + (g * 587) + (b * 114)) / 1000 < 128;
  }
  return true;
});

const relatedPages = ref([]);
const relatedTitle = computed(() => {
  if (pageData.value.type === 'presskit') return 'Explore Presskits';
  return 'Related Experience';
});

const fetchRelated = async () => {
  try {
    const type = pageData.value.type;
    const { data } = await api.getPages({ type });
    relatedPages.value = (data || []).filter(p => p._id !== props.page._id).slice(0, 3);
  } catch (error) {
    console.error("Error fetching related:", error);
  }
};

function setMetaTags() {
  if (!pageData.value || !pageData.value.title) return;
  const title = pageData.value.title + ' | MitoGames';
  useHead({
    title,
    meta: [
      { property: 'og:title', content: title },
      { name: 'description', content: pageData.value.description },
      { property: 'og:image', content: pageData.value.image },
    ]
  });
}

const homeConfig = ref(null);
const fetchHomeConfig = async () => {
  try {
    const res = await api.getHomeConfig("entreterimento_novo", props.lang);
    if (res && res.data) {
      homeConfig.value = props.lang === 'en' ? res.data.en : res.data.pt;
    }
  } catch (e) {
    console.error("Error loading home config:", e);
  }
};

onMounted(() => {
  setMetaTags();
  fetchRelated();
  if (props.page.type === 'presskit') {
    fetchHomeConfig();
  }
  window.scrollTo(0, 0);
});

watch(() => props.page._id, () => {
  setMetaTags();
  fetchRelated();
  if (props.page.type === 'presskit') {
    fetchHomeConfig();
  }
  window.scrollTo(0, 0);
});
</script>

<style>
.presskit-grid {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  width: 100%;
}
.presskit-col-left, .presskit-col-right {
  width: 100%;
}
@media (min-width: 1024px) {
  .presskit-grid {
    flex-direction: row;
    align-items: start;
  }
  .presskit-col-left {
    width: 25%;
    flex-shrink: 0;
    word-break: break-word;
    overflow-wrap: break-word;
  }
  .presskit-col-right {
    width: 75%;
    flex-grow: 1;
    padding-left: 2rem;
  }
}

.presskit-factsheet {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: left;
}
.presskit-factsheet-title {
  font-size: 2rem !important;
  font-weight: 900 !important;
  color: #ffffff !important;
  letter-spacing: -0.02em !important;
  margin-bottom: 1.5rem !important;
  margin-top: 0 !important;
}
.presskit-factsheet-item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.presskit-factsheet-key {
  font-size: 0.85rem !important;
  font-weight: 900 !important;
  color: #a78bfa !important;
  letter-spacing: 0.05em !important;
}
.presskit-factsheet-value {
  font-size: 0.9rem !important;
  color: #94a3b8 !important;
  line-height: 1.5 !important;
  white-space: pre-line !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
}
.presskit-factsheet-value a {
  color: #a78bfa !important;
  text-decoration: underline !important;
  transition: color 0.2s ease;
}
.presskit-factsheet-value a:hover {
  color: #c084fc !important;
}

.bg-light-theme .presskit-factsheet-title {
  color: #000000 !important;
}
.bg-light-theme .presskit-factsheet-key {
  color: #000000 !important;
}
.bg-light-theme .presskit-factsheet-value {
  color: #000000 !important;
}
.bg-light-theme .presskit-factsheet-value a {
  color: #60269E !important;
  text-decoration: underline !important;
}
.bg-light-theme .presskit-factsheet-value a:hover {
  color: #7c3aed !important;
}

.mito-gallery-grid-clean {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  width: 100%;
  align-items: center;
}
.mito-gallery-item-clean {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 calc(25% - 1.125rem);
  min-width: 200px;
  max-width: 100%;
}
@media (max-width: 768px) {
  .mito-gallery-item-clean {
    flex: 0 0 calc(50% - 0.75rem);
  }
}
@media (max-width: 480px) {
  .mito-gallery-item-clean {
    flex: 0 0 100%;
  }
}
.mito-gallery-img-clean {
  width: 100%;
  height: auto;
  max-height: 380px;
  object-fit: contain;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.mito-gallery-img-clean:hover {
  transform: scale(1.02);
}

.presskit-col-right .mito-gallery-block h2,
.presskit-col-right .mito-video-block h2,
.presskit-col-right .mito-faq-block h2 {
  text-transform: none !important;
  text-align: left !important;
  letter-spacing: -0.02em !important;
}

.presskit-col-right .faq-item h4 {
  text-transform: none !important;
  text-align: left !important;
}

.presskit-col-right .mito-cta-block h3 {
  text-transform: none !important;
}

.builder-page-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  color: #CBD5E1;
  font-family: 'Inter', -apple-system, sans-serif;
}

/* Builder blocks styling */
.builder-page-content .mito-compiled-page {
  width: 100%;
}

.builder-page-content .mito-hero-block {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 450px;
  border-radius: 24px;
  padding: 3rem 2rem;
  margin-bottom: 3rem;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.builder-page-content .mito-hero-block h1 {
  font-size: 2.5rem;
  font-weight: 900;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-bottom: 1rem;
}

@media (min-width: 768px) {
  .builder-page-content .mito-hero-block h1 {
    font-size: 4rem;
  }
}

.builder-page-content .mito-hero-block p {
  font-size: 1.125rem;
  color: #E2E8F0;
  max-width: 32rem;
  margin: 0 auto 1.5rem auto;
}

.builder-page-content .mito-btn-download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%) !important;
  color: #FFFFFF !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  padding: 14px 32px !important;
  border-radius: 14px !important;
  box-shadow: 0 10px 25px rgba(124, 58, 237, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
  text-decoration: none !important;
  font-size: 0.85rem !important;
}

.builder-page-content .mito-btn-download:hover {
  transform: translateY(-2px) scale(1.02) !important;
  box-shadow: 0 15px 30px rgba(124, 58, 237, 0.5) !important;
}

.builder-page-content .mito-text-block {
  max-width: 800px;
  margin: 0 auto 3rem auto;
  line-height: 1.8;
  font-size: 1.1rem;
  color: #94A3B8;
}

.builder-page-content .mito-text-block h1,
.builder-page-content .mito-text-block h2,
.builder-page-content .mito-text-block h3,
.builder-page-content .mito-text-block h4 {
  color: #FFFFFF;
  font-weight: 900;
  text-transform: uppercase;
  margin-top: 2rem;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
}

.builder-page-content .mito-text-block h1 { font-size: 2.2rem; }
.builder-page-content .mito-text-block h2 { font-size: 1.8rem; }
.builder-page-content .mito-text-block h3 { font-size: 1.4rem; }

.builder-page-content .mito-text-block p {
  margin-bottom: 1.25rem;
}

.builder-page-content .mito-gallery-block {
  margin-bottom: 4rem;
}

.builder-page-content .mito-gallery-block h2 {
  font-size: 1.75rem;
  font-weight: 900;
  color: #FFFFFF;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 2rem;
  letter-spacing: -0.02em;
}

.builder-page-content .mito-gallery-grid {
  display: grid !important;
  grid-template-cols: repeat(auto-fit, minmax(280px, 1fr)) !important;
  gap: 24px !important;
}

.builder-page-content .mito-gallery-item {
  background-color: rgba(21, 21, 22, 0.5) !important;
  border-radius: 20px !important;
  overflow: hidden !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.builder-page-content .mito-gallery-item:hover {
  transform: translateY(-4px) !important;
  border-color: rgba(168, 85, 247, 0.3) !important;
  box-shadow: 0 15px 40px rgba(168, 85, 247, 0.15) !important;
}

.builder-page-content .mito-video-block {
  max-width: 800px;
  margin: 0 auto 4rem auto;
}

.builder-page-content .mito-video-block h2 {
  font-size: 1.75rem;
  font-weight: 900;
  color: #FFFFFF;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 2rem;
}

.builder-page-content .mito-video-container {
  position: relative !important;
  padding-bottom: 56.25% !important;
  height: 0 !important;
  overflow: hidden !important;
  border-radius: 24px !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5) !important;
}

.builder-page-content .mito-video-container iframe {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  border: none !important;
  border-radius: 24px !important;
}

.builder-page-content .mito-ficha-block {
  max-width: 800px;
  margin: 0 auto 4rem auto;
}

.builder-page-content .mito-ficha-tecnica {
  width: 100% !important;
  border-collapse: collapse !important;
  background-color: rgba(21, 21, 22, 0.3) !important;
  border-radius: 20px !important;
  overflow: hidden !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.builder-page-content .mito-ficha-tecnica th,
.builder-page-content .mito-ficha-tecnica td {
  padding: 18px 24px !important;
  text-align: left !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.builder-page-content .mito-ficha-tecnica th {
  background-color: rgba(255, 255, 255, 0.02) !important;
  color: #FFFFFF !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  font-size: 0.85rem !important;
  letter-spacing: 0.1em !important;
}

.builder-page-content .mito-ficha-tecnica td {
  color: #94A3B8 !important;
  font-size: 0.95rem !important;
}

.builder-page-content .mito-ficha-tecnica tr:hover td {
  background-color: rgba(255, 255, 255, 0.02) !important;
  color: #FFFFFF !important;
}

.builder-page-content .mito-faq-block {
  max-width: 800px;
  margin: 0 auto 4rem auto;
}

.builder-page-content .mito-faq-block h2 {
  font-size: 1.75rem;
  font-weight: 900;
  color: #FFFFFF;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 2rem;
}

.builder-page-content .faq-item {
  padding: 1.5rem !important;
  border-radius: 20px !important;
  background-color: rgba(21, 21, 22, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  transition: all 0.3s ease !important;
  margin-bottom: 1rem !important;
}

.builder-page-content .faq-item:hover {
  border-color: rgba(168, 85, 247, 0.2) !important;
  box-shadow: 0 10px 25px rgba(168, 85, 247, 0.05) !important;
}

.builder-page-content .faq-item h4 {
  font-size: 1.05rem !important;
  font-weight: 950 !important;
  color: #FFFFFF !important;
  text-transform: uppercase !important;
  letter-spacing: 0.02em !important;
  margin-bottom: 0.5rem !important;
}

.builder-page-content .faq-item p {
  color: #94A3B8 !important;
  line-height: 1.7 !important;
  font-size: 0.95rem !important;
}

.builder-page-content .mito-cta-block {
  max-width: 800px !important;
  margin: 0 auto 4rem auto !important;
  text-align: center !important;
  padding: 3rem 2rem !important;
  border-radius: 28px !important;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(0, 0, 0, 0.8) 100%) !important;
  border: 1px solid rgba(168, 85, 247, 0.15) !important;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.5) !important;
}

.builder-page-content .mito-cta-block h3 {
  font-size: 1.75rem !important;
  font-weight: 950 !important;
  color: #FFFFFF !important;
  text-transform: uppercase !important;
  margin-bottom: 0.75rem !important;
}

.builder-page-content .mito-cta-block p {
  color: #94A3B8 !important;
  font-size: 1rem !important;
  max-width: 500px !important;
  margin: 0 auto 1.5rem auto !important;
}

.custom-page-content {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
  color: #CBD5E1;
  font-family: 'Inter', -apple-system, sans-serif;
  line-height: 1.9;
  font-size: 1.2rem;
}

.custom-page-content h1, 
.custom-page-content h2, 
.custom-page-content h3 {
  color: #FFFFFF;
  font-weight: 900;
  margin-top: 3.5em;
  margin-bottom: 1.2em;
  line-height: 1.1;
  letter-spacing: -0.04em;
  text-transform: uppercase;
}

.custom-page-content h1 { font-size: 4rem; }
.custom-page-content h2 { font-size: 2.5rem; }
.custom-page-content h3 { font-size: 1.85rem; }

.custom-page-content p { margin-bottom: 1.8em; }

.custom-page-content a {
  color: #A78BFA;
  text-decoration: none;
  font-weight: 800;
  border-bottom: 2px solid rgba(167, 139, 250, 0.3);
  transition: all 0.3s ease;
}

.custom-page-content a:hover {
  color: #FFFFFF;
  border-bottom-color: #FFFFFF;
  background: rgba(167, 139, 250, 0.1);
}

/* Image Grid Logic in Content */
.custom-page-content .ql-align-center img,
.custom-page-content .ql-align-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.custom-page-content img {
  max-width: 100%;
  border-radius: 32px;
  margin: 1.5rem;
  display: inline-block; /* Allows side-by-side images */
  vertical-align: middle;
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 0.5s ease;
}

.custom-page-content .ql-align-left {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.custom-page-content .ql-align-right {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.custom-page-content .ql-align-center {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Images aligned to left/right take 48% to fit two per row */
.custom-page-content .ql-align-left img,
.custom-page-content .ql-align-right img {
  max-width: 48%;
  margin: 1rem 1%;
}

.custom-page-content .ql-align-center img {
  max-width: 100%;
  margin: 4rem auto;
  display: block;
}

.custom-page-content iframe {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 40px;
  margin: 5rem 0;
  box-shadow: 0 0 60px rgba(131, 72, 242, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.custom-page-content blockquote {
  border-left: 8px solid #8348F2;
  padding: 2.5rem 3.5rem;
  font-style: italic;
  color: #F8FAFC;
  margin: 4rem 0;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 32px 32px 0;
  font-size: 1.4rem;
  box-shadow: inset 0 0 40px rgba(0,0,0,0.2);
}

.custom-page-content .ql-align-center { text-align: center; }
.custom-page-content .ql-align-right { text-align: right; }
.custom-page-content .ql-align-justify { text-align: justify; }

.custom-page-content ul, .custom-page-content ol {
  margin-bottom: 2.5rem;
  padding-left: 2rem;
}

.custom-page-content li {
  margin-bottom: 1rem;
  padding-left: 0.5rem;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-up {
  animation: fadeInUp 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.bg-light-theme {
  color: #1E293B !important;
}
.bg-light-theme .builder-page-content {
  color: #334155 !important;
}
.bg-light-theme .builder-page-content h1,
.bg-light-theme .builder-page-content h2,
.bg-light-theme .builder-page-content h3,
.bg-light-theme .builder-page-content h4,
.bg-light-theme .mito-text-block h1,
.bg-light-theme .mito-text-block h2,
.bg-light-theme .mito-text-block h3,
.bg-light-theme .mito-text-block h4,
.bg-light-theme .mito-hero-block h1,
.bg-light-theme .mito-hero-block p,
.bg-light-theme .mito-gallery-block h2,
.bg-light-theme .mito-video-block h2,
.bg-light-theme .mito-faq-block h2,
.bg-light-theme .faq-item h4,
.bg-light-theme .mito-cta-block h3,
.bg-light-theme .custom-page-content h1,
.bg-light-theme .custom-page-content h2,
.bg-light-theme .custom-page-content h3,
.bg-light-theme .custom-page-content blockquote {
  color: #0F172A !important;
}
.bg-light-theme .faq-item {
  background-color: rgba(0, 0, 0, 0.03) !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
}
.bg-light-theme .faq-item p,
.bg-light-theme .mito-text-block,
.bg-light-theme .mito-text-block p,
.bg-light-theme .builder-page-content .mito-ficha-tecnica td,
.bg-light-theme .mito-cta-block p {
  color: #334155 !important;
}
.bg-light-theme .builder-page-content .mito-ficha-tecnica {
  background-color: rgba(0, 0, 0, 0.02) !important;
  border-color: rgba(0, 0, 0, 0.06) !important;
}
.bg-light-theme .builder-page-content .mito-ficha-tecnica th {
  background-color: rgba(0, 0, 0, 0.04) !important;
  color: #0F172A !important;
}
.bg-light-theme .custom-page-content blockquote {
  background: rgba(0, 0, 0, 0.03) !important;
  color: #1E293B !important;
}
.bg-light-theme .builder-page-content .mito-gallery-item {
  background-color: rgba(0, 0, 0, 0.02) !important;
  border-color: rgba(0, 0, 0, 0.05) !important;
}
</style>
